import { useLayoutEffect, useState, useRef } from "react";
import { loadModules } from "esri-loader";

const useEsriLoader = (
  mapRef,
  options,
  additionalModulesToLoad,
  activeHighlightId
) => {
  const [mapLoading, setMapLoading] = useState(true);
  const [esriLoader, setEsriLoader] = useState();
  const additionalModulesRef = useRef(additionalModulesToLoad);

  useLayoutEffect(() => {
    if (!mapRef) return;
    const additionalModulesPaths = additionalModulesRef
      ? additionalModulesRef.current.map((module) => module.path)
      : [];

    const modulesToLoad = [
      "esri/Map",
      "esri/views/MapView",
      "esri/layers/FeatureLayer",
      "esri/Basemap",
      "esri/geometry/Extent",
      ...additionalModulesPaths,
    ];

    // lazy load the required ArcGIS API for JavaScript modules and CSS
    loadModules(modulesToLoad, {
      css: true,
    }).then(([Map, MapView, FeatureLayer, Basemap, Extent, ...rest]) => {
      setMapLoading(false);

      // OS GB Basemaps
      const OS_Open_Background_Esri_UK = new Basemap({
        portalItem: { id: "a4ee9f2805754f4b9004103f67688bf1" },
      });
      const OS_Open_Gray_Esri_UK = new Basemap({
        portalItem: { id: "93320e50f3f3419795946b1243aadb1e" },
      });
      const OS_Open_Gray_Labels_Esri_UK = new Basemap({
        portalItem: { id: "7be6d1f81ab047fda02107a97079ccd3" },
      });
      const OS_Open_Carto_Esri_UK = new Basemap({
        portalItem: { id: "0bd3a4a6fd674a90a7d0a9e5f36fb59b" },
      });
      const OS_Open_Raster_Esri_UK = new Basemap({
        portalItem: { id: "5b5ff94ca7414a26b75e0d337a023d38" },
      });
      const GB_Dark_Grey_Esri_UK = new Basemap({
        portalItem: { id: "a118075240bc4e4f8062265ecdad0e7e" },
      });

      const mapExtent = new Extent(options.extent);
      const map = new Map({
        basemap: OS_Open_Background_Esri_UK,
      });
      const basemapCollection = [
        OS_Open_Background_Esri_UK,
        OS_Open_Gray_Esri_UK,
        OS_Open_Gray_Labels_Esri_UK,
        OS_Open_Carto_Esri_UK,
        OS_Open_Raster_Esri_UK,
        GB_Dark_Grey_Esri_UK,
      ];

      const view = new MapView({
        container: mapRef.current,
        map,
        extent: mapExtent,
        zoom: options.zoom ? options.zoom : 7,
        popup: {
          autoOpenEnabled: false,
          actions: [], // this will remove zoom to action and all other actions
          dockOptions: {
            buttonEnabled: false,
          },
        },
      });

      // popup click event listener
      view.on("click", async function (viewEvent) {
        const hitTestResponse = await view.hitTest(viewEvent);
        if (hitTestResponse.results.length) {
          let graphic = hitTestResponse.results.filter(function (result) {
            return result.graphic.layer;
          })[0].graphic;
          if (viewEvent.mapPoint) {
            if (graphic.layer.id !== activeHighlightId) {
              view.popup.open({
                location: viewEvent.mapPoint,
                features: [graphic],
              });
              return;
            }
          }
          view.popup.close();
        }
      });

      const additionalModules = additionalModulesRef
        ? Object.fromEntries(
            additionalModulesRef.current.map((module, i) => [
              module.name,
              rest[i],
            ])
          )
        : {};

      view.ui.move("zoom", "bottom-left");

      view
        .when(() => {
          setEsriLoader({
            view,
            FeatureLayer,
            basemapCollection,
            ...additionalModules,
          });
        })
        .catch((e) => {
          console.error(e);
        });

      return () => {
        if (view) {
          // destroy the map view
          view.container = null;
        }
      };
    });
  }, [mapRef, additionalModulesRef, activeHighlightId, options]);

  return {
    mapLoading,
    esriLoader,
  };
};

const geoProcessor = (gpUrl) => {
  return new Promise((resolve, reject) => {
    if (!gpUrl) {
      reject();
      return;
    }
    loadModules(["esri/tasks/Geoprocessor"]).then(([Geoprocessor]) => {
      resolve(new Geoprocessor(gpUrl));
    });
  });
};

export { useEsriLoader, geoProcessor };
